import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './index.css';

const FoundersCircles = () => {
    const [openFaq, setOpenFaq] = useState(0);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: 'Who can apply to Founder Circles?',
            a: 'Founders and active co-founders building operating companies may apply. Matching depends on stage, context and circle availability.'
        },
        {
            q: 'How quickly will I be matched?',
            a: 'We review applications weekly and match founders into new cohorts as compatible stage and operating contexts align.'
        },
        {
            q: 'Are sessions online or in person?',
            a: 'Core monthly circle sessions are held virtually in private rooms, complemented by optional regional cohort meetups.'
        },
        {
            q: 'What is expected from every member?',
            a: 'Active participation, strict confidentiality, honest challenge sharing, and consistent attendance in monthly sessions.'
        }
    ];

    return (
        <div className="fc-page">
            {/* =========================================================================
          SECTION 1: HERO SECTION
          ========================================================================= */}
            <section className="fc-hero-section">
                <div className="fc-hero-left">
                    <div className="fc-tag">FOUNDER CIRCLES</div>
                    <h1 className="fc-hero-title">
                        Build in company.<br />
                        Decide in confidence.<br />
                        Grow with your circle.
                    </h1>
                    <p className="fc-hero-desc">
                        Curated peer groups for founders at similar stages—built for honest conversations, useful accountability and relationships that compound.
                    </p>

                    <div className="fc-hero-actions">
                        <Link to="/founderscircleform" style={{ textDecoration: 'none' }}>
                            <button className="fc-btn-primary">Find your circle</button>
                        </Link>
                        <button className="fc-btn-outline">How circles work</button>
                    </div>

                    <div className="fc-hero-badge-check">
                        <span className="fc-chk-icon">✓</span>
                        <span>Curated by stage, role and operating context.</span>
                    </div>
                </div>

                <div className="fc-hero-right">
                    <div className="fc-hero-circle-diagram">
                        {/* Context Tooltips */}
                        <div className="fc-float-badge badge-next-session">
                            <span className="fc-f-icon">📅</span>
                            <div>
                                <strong>Next session</strong>
                                <p>Thursday · 5:00 PM</p>
                            </div>
                        </div>

                        <div className="fc-float-badge badge-theme">
                            <span className="fc-f-icon">💬</span>
                            <div>
                                <strong>Current theme</strong>
                                <p>Hiring the first leadership layer</p>
                            </div>
                        </div>

                        <div className="fc-float-badge badge-shared">
                            <span className="fc-f-icon">👥</span>
                            <div>
                                <strong>Shared this week</strong>
                                <p>12 useful introductions</p>
                            </div>
                        </div>

                        {/* Circle Nodes */}
                        <div className="fc-orbit-ring">
                            <div className="fc-node node-1"><div className="fc-avatar av-1">👨‍💼</div></div>
                            <div className="fc-node node-2"><div className="fc-avatar av-2">👩‍🔬</div></div>
                            <div className="fc-node node-3"><div className="fc-avatar av-3">👩‍💼</div></div>
                            <div className="fc-node node-4"><div className="fc-avatar av-4">👨‍💻</div></div>
                            <div className="fc-node node-5"><div className="fc-avatar av-5">👩‍💻</div></div>
                            <div className="fc-node node-6"><div className="fc-avatar av-6">👨‍🎨</div></div>

                            <div className="fc-center-node">
                                <div className="fc-center-icon">👥</div>
                                <h3>Your Circle</h3>
                                <span>6 founders</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hero Strip */}
            <div className="fc-hero-strip">
                <div className="fc-strip-item">
                    <span className="fc-strip-icon">👥</span>
                    <span>6–8 founders per circle</span>
                </div>
                <div className="fc-strip-item">
                    <span className="fc-strip-icon">📅</span>
                    <span>Monthly private sessions</span>
                </div>
                <div className="fc-strip-item">
                    <span className="fc-strip-icon">🤝</span>
                    <span>Curated introductions</span>
                </div>
                <div className="fc-strip-item">
                    <span className="fc-strip-icon">🛡️</span>
                    <span>Confidential by design</span>
                </div>
            </div>

            {/* Hero Sub-cards: Why A Circle */}
            <div className="fc-why-subcards-section">
                <div className="fc-tag">WHY A CIRCLE</div>
                <h2 className="fc-sec-title-sm">The conversations you cannot have everywhere.</h2>

                <div className="fc-why-grid">
                    <div className="fc-why-card">
                        <div className="fc-wc-icon-box">💬</div>
                        <div>
                            <h4>Operator honesty</h4>
                            <p>Discuss the real problem, not the polished update.</p>
                        </div>
                    </div>

                    <div className="fc-why-card">
                        <div className="fc-wc-icon-box">📈</div>
                        <div>
                            <h4>Useful accountability</h4>
                            <p>Leave every session with one clear next move.</p>
                        </div>
                    </div>

                    <div className="fc-why-card">
                        <div className="fc-wc-icon-box">👥</div>
                        <div>
                            <h4>Compounding trust</h4>
                            <p>Build relationships that become more valuable over time.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================================================
          SECTION 2: HOW CIRCLES WORK (Timeline + Session Agenda)
          ========================================================================= */}
            <section className="fc-section fc-how-section">
                <div className="fc-sec-header text-center">
                    <div className="fc-tag centered-tag">HOW CIRCLES WORK</div>
                    <h2 className="fc-sec-title">The right room starts with the right fit.</h2>
                    <p className="fc-sec-desc">
                        Every circle is deliberately composed around stage, role and operating context—so the conversation is useful from the first session.
                    </p>
                </div>

                {/* 4 Steps Horizontal */}
                <div className="fc-steps-timeline">
                    <div className="fc-timeline-bar"></div>

                    <div className="fc-steps-grid">
                        <div className="fc-step-item">
                            <div className="fc-step-num-bubble">01</div>
                            <div className="fc-step-card">
                                <div className="fc-sc-icon">🪪</div>
                                <h4>Tell us where you are</h4>
                                <p>Share your company stage, role, priorities and the decisions currently on your desk.</p>
                            </div>
                        </div>

                        <div className="fc-step-item">
                            <div className="fc-step-num-bubble">02</div>
                            <div className="fc-step-card">
                                <div className="fc-sc-icon">⚙️</div>
                                <h4>We curate the fit</h4>
                                <p>We match for relevance while avoiding direct competition and obvious conflicts.</p>
                            </div>
                        </div>

                        <div className="fc-step-item">
                            <div className="fc-step-num-bubble">03</div>
                            <div className="fc-step-card">
                                <div className="fc-sc-icon">👥</div>
                                <h4>Meet your circle</h4>
                                <p>Join six to eight founders in a private, facilitated monthly session.</p>
                            </div>
                        </div>

                        <div className="fc-step-item">
                            <div className="fc-step-num-bubble">04</div>
                            <div className="fc-step-card">
                                <div className="fc-sc-icon">🚩</div>
                                <h4>Build momentum</h4>
                                <p>Track commitments, exchange context and compound trust between sessions.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Dark Container: Inside a Monthly Circle Session */}
                <div className="fc-dark-agenda-card">
                    <div className="fc-agenda-left">
                        <h3>Inside a monthly circle session</h3>
                        <p className="fc-agenda-sub">A clear 75-minute agenda designed for depth and action.</p>

                        <div className="fc-agenda-timeline">
                            <div className="fc-agenda-item">
                                <div className="fc-agenda-dot"></div>
                                <div>
                                    <strong>10 min · Check-in</strong>
                                    <p>Set the stage and share brief updates.</p>
                                </div>
                            </div>
                            <div className="fc-agenda-item">
                                <div className="fc-agenda-dot"></div>
                                <div>
                                    <strong>20 min · Founder hot seat</strong>
                                    <p>One founder gets targeted support from the group.</p>
                                </div>
                            </div>
                            <div className="fc-agenda-item">
                                <div className="fc-agenda-dot"></div>
                                <div>
                                    <strong>30 min · Peer problem-solving</strong>
                                    <p>The group tackles real challenges, together.</p>
                                </div>
                            </div>
                            <div className="fc-agenda-item">
                                <div className="fc-agenda-dot"></div>
                                <div>
                                    <strong>15 min · Commitments</strong>
                                    <p>Close with clear next steps and accountability.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="fc-agenda-right">
                        <div className="fc-agenda-widget">
                            <div className="fc-aw-header">
                                <strong>Founder Circle · Growth Stage</strong>
                                <span className="fc-aw-green">● Private session</span>
                            </div>
                            <div className="fc-aw-avatars">
                                <div className="fc-av-pill">👨‍💼</div>
                                <div className="fc-av-pill">👩‍💼</div>
                                <div className="fc-av-pill">👨‍💻</div>
                                <div className="fc-av-pill">👩‍🔬</div>
                                <div className="fc-av-pill">👨‍🎨</div>
                                <div className="fc-av-pill">👩‍💻</div>
                            </div>
                            <div className="fc-aw-row">
                                <span>🎯 <strong>Today's focus</strong> · Leadership hiring</span>
                            </div>
                            <div className="fc-aw-row">
                                <span>📄 <strong>Shared notes</strong> · 8</span>
                            </div>
                            <div className="fc-aw-row">
                                <span>📅 <strong>Next check-in</strong> · 12 September</span>
                            </div>
                            <button className="fc-btn-view-format">View session format</button>
                        </div>
                    </div>
                </div>

                <div className="fc-agenda-bottom-strip">
                    <div className="fc-abs-item">✓ Facilitated with structure</div>
                    <div className="fc-abs-sep"></div>
                    <div className="fc-abs-item">🚫 No pitching inside circles</div>
                    <div className="fc-abs-sep"></div>
                    <div className="fc-abs-item">🔒 Confidential conversations</div>
                    <div className="fc-abs-sep"></div>
                    <div className="fc-abs-item">✓ Action after every session</div>
                </div>
            </section>

            {/* =========================================================================
          SECTION 3: FIND YOUR FIT (3 Circle Tier Cards)
          ========================================================================= */}
            <section className="fc-section fc-fit-section">
                <div className="fc-sec-header text-center">
                    <div className="fc-tag centered-tag">FIND YOUR FIT</div>
                    <h2 className="fc-sec-title">A circle built around where you are going.</h2>
                    <p className="fc-sec-desc">
                        Stage creates context. Shared ambition creates value. Choose the room closest to the decisions in front of you.
                    </p>
                </div>

                <div className="fc-fit-grid">
                    {/* Card 1: Early-Stage */}
                    <div className="fc-fit-card border-green">
                        <div className="fc-fit-icon-circle bg-green-light">🌱</div>
                        <span className="fc-fit-category text-green">EARLY-STAGE CIRCLE</span>
                        <h3>From conviction<br />to repeatability</h3>
                        <div className="fc-fit-avatars-row">
                            <span>👨‍💼</span><span>👩‍💼</span><span>👨‍💻</span><span>👩‍🔬</span><span>👨‍🎨</span><span>👩‍💻</span>
                        </div>
                        <p className="fc-fit-desc">
                            For pre-seed and seed founders validating the wedge, winning early customers and building the first operating rhythm.
                        </p>
                        <div className="fc-fit-tags">
                            <span>Product-market fit</span>
                            <span>First hires</span>
                            <span>Founder-led sales</span>
                        </div>
                        <div className="fc-fit-footer">
                            <span className="fc-fit-meta">6–8 founders · Monthly</span>
                            <button className="fc-btn-fit-outline">Explore early-stage circles</button>
                        </div>
                    </div>

                    {/* Card 2: Growth */}
                    <div className="fc-fit-card border-purple highlighted-card">
                        <div className="fc-featured-badge">Most active</div>
                        <div className="fc-fit-icon-circle bg-purple-light">📈</div>
                        <span className="fc-fit-category text-purple">GROWTH CIRCLE</span>
                        <h3>From traction to<br />a real company</h3>
                        <div className="fc-fit-avatars-row">
                            <span>👨‍💼</span><span>👩‍💼</span><span>👨‍💻</span><span>👩‍🔬</span><span>👨‍🎨</span><span>👩‍💻</span>
                        </div>
                        <p className="fc-fit-desc">
                            For founders building leadership, scaling revenue and turning fast growth into a durable operating system.
                        </p>
                        <div className="fc-fit-tags">
                            <span>Leadership team</span>
                            <span>Growth systems</span>
                            <span>Capital planning</span>
                        </div>
                        <div className="fc-fit-footer">
                            <span className="fc-fit-meta">6–8 founders · Monthly</span>
                            <button className="fc-btn-fit-solid">Explore growth circles</button>
                        </div>
                    </div>

                    {/* Card 3: Scale */}
                    <div className="fc-fit-card border-amber">
                        <div className="fc-fit-icon-circle bg-amber-light">🏛️</div>
                        <span className="fc-fit-category text-amber">SCALE CIRCLE</span>
                        <h3>From company<br />to institution</h3>
                        <div className="fc-fit-avatars-row">
                            <span>👨‍💼</span><span>👩‍💼</span><span>👨‍💻</span><span>👩‍🔬</span><span>👨‍🎨</span><span>👩‍💻</span>
                        </div>
                        <p className="fc-fit-desc">
                            For experienced founders navigating executive depth, governance, category leadership and long-term leverage.
                        </p>
                        <div className="fc-fit-tags">
                            <span>Executive design</span>
                            <span>Governance</span>
                            <span>Strategic leverage</span>
                        </div>
                        <div className="fc-fit-footer">
                            <span className="fc-fit-meta">6–8 founders · Monthly</span>
                            <button className="fc-btn-fit-outline amber-btn">Explore scale circles</button>
                        </div>
                    </div>
                </div>

                {/* What Every Circle Shares Strip */}
                <div className="fc-shares-banner">
                    <h4>What every circle shares</h4>
                    <div className="fc-shares-grid">
                        <div><span>👥</span> Similar operating context</div>
                        <div><span>🚫</span> No direct competitors</div>
                        <div><span>🔒</span> Private monthly sessions</div>
                        <div><span>💬</span> Curated peer exchange</div>
                        <div><span>♾️</span> Long-term continuity</div>
                    </div>
                    <p className="fc-shares-note">
                        Your circle stays intentionally small so every voice, challenge and commitment matters.
                    </p>
                </div>

                <div className="fc-fit-bottom-link">
                    <a href="#apply">Not sure where you fit? Tell us about your company →</a>
                </div>
            </section>

            {/* =========================================================================
          SECTION 4: BETWEEN SESSIONS (Dark Workspace Dashboard)
          ========================================================================= */}
            <section className="fc-dark-section">
                <div className="fc-dark-split">
                    <div className="fc-dark-text-side">
                        <div className="fc-tag text-purple-light">BETWEEN SESSIONS</div>
                        <h2 className="fc-dark-h2">
                            The circle keeps working after the call ends.
                        </h2>
                        <p className="fc-dark-p">
                            Commitments stay visible. Useful context moves quickly. Introductions happen when another founder can genuinely help.
                        </p>

                        <ul className="fc-dark-checklist">
                            <li><span className="fc-chk-icon-purple">✓</span> Private member exchange</li>
                            <li><span className="fc-chk-icon-purple">✓</span> Visible monthly commitments</li>
                            <li><span className="fc-chk-icon-purple">✓</span> Curated asks and introductions</li>
                        </ul>

                        <button className="fc-btn-purple">See the member experience</button>
                    </div>

                    {/* Member Space Mockup Dashboard */}
                    <div className="fc-dark-window-side">
                        <div className="fc-member-app">
                            <div className="fc-ma-head">
                                <strong>Growth Circle · Member Space</strong>
                                <span className="fc-ma-pill">● Circle members only</span>
                            </div>
                            <div className="fc-ma-avatars">
                                <span>👨‍💼</span><span>👩‍💼</span><span>👨‍💻</span><span>👩‍🔬</span><span>👨‍🎨</span><span>👩‍💻</span>
                            </div>

                            <div className="fc-ma-body">
                                <div className="fc-ma-nav">
                                    <div className="fc-ma-nav-item active">🏠 Home</div>
                                    <div className="fc-ma-nav-item">◎ Commitments</div>
                                    <div className="fc-ma-nav-item">💬 Asks</div>
                                    <div className="fc-ma-nav-item">👥 Introductions</div>
                                </div>

                                <div className="fc-ma-main">
                                    <h5>This month's commitments</h5>
                                    <div className="fc-commit-row">
                                        <span>◎ Hire VP Sales</span>
                                        <span className="fc-initials">AK</span>
                                        <span className="fc-status-blue">In progress</span>
                                    </div>
                                    <div className="fc-commit-row">
                                        <span>💼 Close enterprise pilot</span>
                                        <span className="fc-initials">RM</span>
                                        <span className="fc-status-green">On track</span>
                                    </div>
                                    <div className="fc-commit-row">
                                        <span>📄 Prepare board update</span>
                                        <span className="fc-initials">NS</span>
                                        <span className="fc-status-ready-pill">Ready</span>
                                    </div>
                                </div>

                                <div className="fc-ma-side">
                                    <h5>Open asks</h5>
                                    <div className="fc-ask-card">
                                        <p>Looking for a fintech compliance lead</p>
                                        <button className="fc-btn-offer">Offer help</button>
                                    </div>
                                    <div className="fc-ask-card">
                                        <p>Seeking advice on ESOP refresh</p>
                                        <button className="fc-btn-offer">Offer help</button>
                                    </div>

                                    <div className="fc-intro-toast">
                                        <strong>Warm introduction</strong>
                                        <div className="fc-it-line">
                                            <span>👩‍💼</span> —— <span>👨‍🎨</span>
                                        </div>
                                        <p>Rhea connected Arjun with an enterprise HR leader.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="fc-ma-footer-toast">
                                👥 9 useful exchanges this month
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4 Bottom Dark Cards */}
                <div className="fc-dark-four-grid">
                    <div className="fc-d4-card">
                        <div className="fc-d4-icon">📈</div>
                        <div>
                            <h4>Decisions move faster</h4>
                            <p>Learn from patterns peers have already seen.</p>
                        </div>
                    </div>
                    <div className="fc-d4-card">
                        <div className="fc-d4-icon">✓</div>
                        <div>
                            <h4>Accountability stays real</h4>
                            <p>Commitments return at the next session.</p>
                        </div>
                    </div>
                    <div className="fc-d4-card">
                        <div className="fc-d4-icon">💬</div>
                        <div>
                            <h4>Help becomes specific</h4>
                            <p>Clear asks create useful responses.</p>
                        </div>
                    </div>
                    <div className="fc-d4-card">
                        <div className="fc-d4-icon">👥</div>
                        <div>
                            <h4>Trust compounds</h4>
                            <p>The group gets more valuable over time.</p>
                        </div>
                    </div>
                </div>

                <p className="fc-dark-subnote">Built for useful proximity—not endless community noise.</p>
            </section>

            {/* =========================================================================
          SECTION 5: TRUST BY DESIGN
          ========================================================================= */}
            <section className="fc-section fc-trust-section">
                <div className="fc-trust-header">
                    <div className="fc-th-left">
                        <div className="fc-tag">TRUST BY DESIGN</div>
                        <h2 className="fc-sec-title">
                            Honest conversations need more than good intentions.
                        </h2>
                        <p className="fc-sec-desc">
                            Every circle is protected by deliberate matching, clear participation rules and skilled facilitation—so founders can discuss what is actually difficult.
                        </p>
                    </div>

                    <div className="fc-th-right">
                        <div className="fc-shield-orbit">
                            <div className="fc-shield-center">
                                🛡️
                                <span>Private by default</span>
                            </div>
                            <div className="fc-so-avatar o-1">👨‍💼</div>
                            <div className="fc-so-avatar o-2">👩‍💼</div>
                            <div className="fc-so-avatar o-3">👨‍💻</div>
                            <div className="fc-so-avatar o-4">👩‍🔬</div>
                            <div className="fc-so-avatar o-5">👨‍🎨</div>
                            <div className="fc-so-avatar o-6">👩‍💻</div>
                        </div>
                    </div>
                </div>

                {/* 4 Pillars */}
                <div className="fc-pillars-grid">
                    <div className="fc-pillar-card">
                        <span className="fc-pillar-num">01</span>
                        <div className="fc-pillar-icon">🔄</div>
                        <h4>Conflict-aware matching</h4>
                        <p>We screen for direct competition, sensitive overlap and obvious commercial conflicts.</p>
                    </div>
                    <div className="fc-pillar-card">
                        <span className="fc-pillar-num">02</span>
                        <div className="fc-pillar-icon">🔒</div>
                        <h4>Confidentiality commitment</h4>
                        <p>What is shared inside a circle stays inside the circle.</p>
                    </div>
                    <div className="fc-pillar-card">
                        <span className="fc-pillar-num">03</span>
                        <div className="fc-pillar-icon">🧭</div>
                        <h4>Structured facilitation</h4>
                        <p>A trained facilitator protects time, depth and balanced participation.</p>
                    </div>
                    <div className="fc-pillar-card">
                        <span className="fc-pillar-num">04</span>
                        <div className="fc-pillar-icon">🤝</div>
                        <h4>Founder-first conduct</h4>
                        <p>No selling, recruiting pressure, fundraising pitches or performative updates.</p>
                    </div>
                </div>

                {/* The Agreement vs When Not A Fit Comparison */}
                <div className="fc-rules-split">
                    <div className="fc-agreement-card">
                        <h4>The circle agreement</h4>
                        <ul>
                            <li><span className="fc-chk-blue">✓</span> Speak from direct experience</li>
                            <li><span className="fc-chk-blue">✓</span> Ask before giving advice</li>
                            <li><span className="fc-chk-blue">✓</span> Protect member confidentiality</li>
                            <li><span className="fc-chk-blue">✓</span> Declare relevant conflicts</li>
                            <li><span className="fc-chk-blue">✓</span> Follow through on commitments</li>
                        </ul>
                        <button className="fc-btn-principles">Read the circle principles</button>
                    </div>

                    <div className="fc-not-fit-card">
                        <h4>When a circle is not the right fit</h4>
                        <ul>
                            <li><span className="fc-minus-circle">−</span> You want an audience for a pitch</li>
                            <li><span className="fc-minus-circle">−</span> You need transactional networking</li>
                            <li><span className="fc-minus-circle">−</span> You cannot attend consistently</li>
                            <li><span className="fc-minus-circle">−</span> You are unwilling to discuss real challenges</li>
                        </ul>
                        <p className="fc-not-fit-note">
                            The strongest circles are built on contribution, candour and continuity.
                        </p>
                    </div>
                </div>

                {/* 4 Bottom Badges */}
                <div className="fc-trust-bottom-bar">
                    <div>👥 Small by design</div>
                    <div className="fc-tbb-sep"></div>
                    <div>🛡️ Screened for conflicts</div>
                    <div className="fc-tbb-sep"></div>
                    <div>🧭 Facilitated with care</div>
                    <div className="fc-tbb-sep"></div>
                    <div>🔒 Protected by commitment</div>
                </div>
            </section>

            {/* =========================================================================
          SECTION 6: JOIN A CIRCLE (FAQ & Blue CTA Footer Banner)
          ========================================================================= */}
            <section className="fc-section fc-join-section" id="apply">
                <div className="fc-join-split">
                    <div className="fc-join-left">
                        <div className="fc-tag">JOIN A CIRCLE</div>
                        <h2 className="fc-sec-title">
                            A thoughtful match starts with a short application.
                        </h2>
                        <p className="fc-sec-desc">
                            Tell us where you are, what you are building and which decisions matter now. We will speak with you before suggesting a circle.
                        </p>

                        <div className="fc-join-steps">
                            <div className="fc-js-item">
                                <span className="fc-js-num">01</span>
                                <div>
                                    <strong>Apply</strong>
                                    <p>Share your stage, role and current priorities.</p>
                                </div>
                            </div>
                            <div className="fc-js-item">
                                <span className="fc-js-num">02</span>
                                <div>
                                    <strong>Fit conversation</strong>
                                    <p>A short call to understand context and expectations.</p>
                                </div>
                            </div>
                            <div className="fc-js-item">
                                <span className="fc-js-num">03</span>
                                <div>
                                    <strong>Circle invitation</strong>
                                    <p>Join when the right group and timing align.</p>
                                </div>
                            </div>
                        </div>

                        <div className="fc-guarantee-note">
                            🛡️ Submitting an application does not guarantee placement.
                        </div>
                    </div>

                    <div className="fc-join-right">
                        <h3>Frequently asked questions</h3>
                        <div className="fc-faq-list">
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className={`fc-faq-card ${openFaq === index ? 'active' : ''}`}
                                    onClick={() => toggleFaq(index)}
                                >
                                    <div className="fc-faq-head">
                                        <span className="fc-faq-q">{faq.q}</span>
                                        <span className="fc-faq-toggle">{openFaq === index ? '−' : '+'}</span>
                                    </div>
                                    {openFaq === index && <p className="fc-faq-a">{faq.a}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Big Blue CTA Banner */}
                <div className="fc-bottom-cta-banner">
                    <div className="fc-cta-left">
                        <span className="fc-cta-tag">YOUR NEXT USEFUL ROOM</span>
                        <h3 className="fc-cta-h3">
                            Build with founders who understand the weight of the decision.
                        </h3>
                        <p className="fc-cta-p">
                            Apply for a curated circle built around relevance, honesty and long-term trust.
                        </p>
                        <div className="fc-cta-btns">
                            <Link to="/founderscircleform" style={{ textDecoration: 'none' }}>
                                <button className="fc-btn-white">Apply to Founder Circles</button>
                            </Link>
                            <button className="fc-btn-trans">Talk to the team</button>
                        </div>
                    </div>

                    <div className="fc-cta-right">
                        <div className="fc-cta-orbit-box">
                            <div className="fc-cta-center">
                                <strong>Your next<br />circle</strong>
                            </div>
                            <div className="fc-cta-node cn-1">👨‍💼</div>
                            <div className="fc-cta-node cn-2">👩‍💼</div>
                            <div className="fc-cta-node cn-3">👨‍💻</div>
                            <div className="fc-cta-node cn-4">👩‍🔬</div>
                            <div className="fc-cta-node cn-5">👨‍🎨</div>
                            <div className="fc-cta-node cn-6">👩‍💻</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FoundersCircles;